import React, { useCallback, useMemo } from 'react';
import { CloudUploadIcon } from '@/components/Icons';
import cn from '@/utils/cn';
import useHomeContext from '@/hooks/useHomeContext';
import { addTransactionAttachment } from '@/services/transactionHistory';
import { NotificationType } from '@/context/types/Notification';
import { useDropzone } from 'react-dropzone';
import { TAttachment } from '@/services/types/TransactionData';

interface UploadAttachmentSectionProps {
  transactionId: string;
  attachmentCount: number;
  onFinishUpload?: (attachment: TAttachment) => void;
}

export const MAX_ATTACHMENTS = 5;
export const MAX_ATTACHMENT_SIZE = 5;

const UploadAttachmentSection: React.FC<UploadAttachmentSectionProps> = ({
  transactionId,
  attachmentCount,
  onFinishUpload
}) => {
  const { triggerNotification, setSelectedTransaction, setError, setCurrentPageTransactions } =
    useHomeContext()!;

  /* eslint-disable @typescript-eslint/no-explicit-any */
  const onDrop = useCallback(async (acceptedFiles: any, rejectedFiles: any) => {
    if (rejectedFiles.length > 0) {
      const error = rejectedFiles[0].errors[0];

      let errorMessage;

      switch (error.code) {
        case 'too-many-files':
          errorMessage = `Too many files, max total of ${MAX_ATTACHMENTS} attachments`;
          break;
        case 'file-invalid-type':
          errorMessage = 'Invalid file type, only images and PDFs are accepted';
          break;
        case 'file-too-large':
          errorMessage = `File too large, max size limit is ${MAX_ATTACHMENT_SIZE} MB`;
          break;
        default:
          errorMessage = error.message;
      }

      triggerNotification(NotificationType.ATTACHMENT_UPLOAD_ERROR, { error: errorMessage });
    } else {
      let error = false;

      await Promise.all(
        acceptedFiles.map(async (file: any) => {
          const formData = new FormData();

          formData.append('attachment', file);

          await addTransactionAttachment(transactionId, formData)
            .then((response) => {
              setCurrentPageTransactions((currTxns) =>
                currTxns.map((txn) =>
                  txn.data.id === transactionId
                    ? {
                        ...txn,
                        data: {
                          ...txn.data,
                          hasAttachments: true,
                          receiptExemptionReason: undefined
                        }
                      }
                    : txn
                )
              );

              // update the transaction details receipt indicator
              setSelectedTransaction((prev) =>
                prev
                  ? {
                      ...prev,
                      data: {
                        ...prev.data,
                        hasAttachments: true,
                        receiptExemptionReason: undefined
                      }
                    }
                  : prev
              );

              if (onFinishUpload) {
                onFinishUpload({
                  id: response.data.id,
                  name: response.data.name,
                  type: response.data.type,
                  state: response.data.state,
                  url: response.data.url
                });
              }
            })
            .catch(() => {
              error = true;
            });
        })
      );

      if (error) {
        setError(
          'An error has occurred adding attachment(s) to this transaction. Please try another file or contact support if the issue persists.'
        );
      } else {
        const notificationTitle = acceptedFiles.length > 1 ? 'Attachments' : 'Attachment';
        const notificationPrefix = acceptedFiles.length > 1 ? 'Attachments were' : 'Attachment was';

        triggerNotification(NotificationType.ATTACHMENT_ADDED, {
          title: notificationTitle,
          prefix: notificationPrefix
        });
      }
    }
  }, []);

  const { getRootProps, getInputProps, isDragAccept, isDragReject } = useDropzone({
    onDrop,
    maxFiles: 5 - attachmentCount,
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': []
    },
    maxSize: 5 * 1024 * 1024 // 5MB
  });

  const borderColor = useMemo(() => {
    if (isDragAccept) {
      return 'border-emerge-teal bg-opacity-75';
    } else if (isDragReject) {
      return 'border-error-darkred bg-emerge-orange-4 cursor-no-drop';
    } else {
      return 'border-emerge-teal';
    }
  }, [isDragAccept, isDragReject]);

  const textColor = useMemo(() => {
    if (isDragReject) {
      return 'text-error-darkred cursor-no-drop';
    } else {
      return 'text-dark-teal-30';
    }
  }, [isDragReject]);

  return (
    <div className="py-4">
      <div className="flex flex-col gay-y-4 cursor-pointer">
        <div className="flex flex-col gap-y-4 items-center justify-center">
          <div
            {...getRootProps()}
            className={cn(
              'border border-dashed bg-emerge-teal-3 bg-opacity-20 min-w-56 w-full py-8 rounded-md',
              borderColor
            )}>
            <input {...getInputProps()} />
            <p
              className={cn(
                textColor,
                'flex flex-col items-center justify-center min-w-36 w-full'
              )}>
              <CloudUploadIcon className={textColor} size="2xl" />
              <span className="text-base">Drag and drop your file here</span>
              <span className="text-xs">
                You can upload up to {MAX_ATTACHMENTS} PDF and image files ({MAX_ATTACHMENT_SIZE}MB
                file size limit)
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadAttachmentSection;
